import fetch from 'unfetch';
import SecureLS from 'secure-ls';

const LEGACY_SSO_STORAGE_KEY = 'ags_sso_staff';
const SSO_SESSION_TOKEN_KEY = 'sis_customer_sso_token';
const SSO_QUERY_KEYS = ['sso_token', 'sso_code'];
const LOGGED_OUT_SSO_TOKEN_KEY = 'sis_customer_logged_out_sso_token';
const SSO_SESSION_CHANNEL_NAME = 'sis_customer_sso_session';
const SSO_SESSION_TAB_ID = `${Date.now()}_${Math.random()}`;
let cachedCurrentStaff = null;
let ssoSessionChannel = null;

const clearLegacySsoStorage = () => {
  if (localStorage.getItem(LEGACY_SSO_STORAGE_KEY)) {
    localStorage.removeItem(LEGACY_SSO_STORAGE_KEY);
  }
}

const clearPersistedVuexState = () => {
  const ls = new SecureLS({isCompression: false});
  ls.remove('cms');
}

const clearSsoSessionToken = () => {
  sessionStorage.removeItem(SSO_SESSION_TOKEN_KEY);
}

const clearCurrentStaffCache = (store) => {
  cachedCurrentStaff = null;
  store?.commit('app/setAuthCurrentStaff', null);
}

const canUseClientStorage = () => {
  return process.client && typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

const getStoredSsoSessionToken = () => {
  return sessionStorage.getItem(SSO_SESSION_TOKEN_KEY);
}

const setLoggedOutSsoToken = (token) => {
  if (token) sessionStorage.setItem(LOGGED_OUT_SSO_TOKEN_KEY, token);
}

export const hasLoggedOutSsoToken = () => {
  return !!sessionStorage.getItem(LOGGED_OUT_SSO_TOKEN_KEY);
}

const clearLocalSsoTokenForLogout = (token) => {
  if (token) setLoggedOutSsoToken(token);
  clearSsoSessionToken();
}

const registerSsoSessionTokenBridge = () => {
  if (!canUseClientStorage() || ssoSessionChannel || typeof BroadcastChannel === 'undefined') return;

  ssoSessionChannel = new BroadcastChannel(SSO_SESSION_CHANNEL_NAME);
  ssoSessionChannel.onmessage = (event) => {
    if (event?.data?.type === 'sso_session_logout') {
      if (event.data.tabId === SSO_SESSION_TAB_ID) return;
      clearLocalSsoTokenForLogout(event.data.token);
      clearCurrentStaffCache();
      return;
    }

    if (event?.data?.type !== 'request_sso_session_token' || hasLoggedOutSsoToken()) return;

    const token = getStoredSsoSessionToken();
    if (!token) return;

    ssoSessionChannel.postMessage({
      type: 'sso_session_token',
      requestId: event.data.requestId,
      token,
    });
  };
}

const copySsoSessionTokenFromOpener = () => {
  if (!canUseClientStorage() || !window.opener || hasLoggedOutSsoToken()) return null;

  try {
    const token = window.opener.sessionStorage?.getItem(SSO_SESSION_TOKEN_KEY);
    if (token) sessionStorage.setItem(SSO_SESSION_TOKEN_KEY, token);
    return token || null;
  } catch (e) {
    return null;
  }
}

const getSsoSessionToken = () => {
  registerSsoSessionTokenBridge();

  const token = sessionStorage.getItem(SSO_SESSION_TOKEN_KEY);
  if (token) return token;

  return copySsoSessionTokenFromOpener();
}

const requestSsoSessionTokenFromOpenTabs = () => {
  const token = getSsoSessionToken();
  if (token || hasLoggedOutSsoToken() || typeof BroadcastChannel === 'undefined') return Promise.resolve(token);

  return new Promise(resolve => {
    const requestId = `${Date.now()}_${Math.random()}`;
    const channel = new BroadcastChannel(SSO_SESSION_CHANNEL_NAME);
    const timeoutId = setTimeout(() => {
      channel.close();
      resolve(null);
    }, 500);

    channel.onmessage = (event) => {
      if (event?.data?.type !== 'sso_session_token' || event.data.requestId !== requestId) return;

      clearTimeout(timeoutId);
      channel.close();
      setSsoSessionToken(event.data.token);
      resolve(event.data.token);
    };

    channel.postMessage({type: 'request_sso_session_token', requestId});
  });
}

const getAuthRequestToken = async () => {
  const token = getSsoSessionToken();
  if (token) return token;

  return await requestSsoSessionTokenFromOpenTabs();
}

export const hasSsoSessionToken = () => {
  return !!getSsoSessionToken();
}

const setSsoSessionToken = (token) => {
  if (token) sessionStorage.setItem(SSO_SESSION_TOKEN_KEY, token);
}

export const markLoggedOutSsoToken = () => {
  const token = getStoredSsoSessionToken();
  setLoggedOutSsoToken(token);

  if (typeof BroadcastChannel === 'undefined') return;

  const channel = new BroadcastChannel(SSO_SESSION_CHANNEL_NAME);
  channel.postMessage({type: 'sso_session_logout', tabId: SSO_SESSION_TAB_ID, token});
  channel.close();
}

const clearLoggedOutSsoToken = () => {
  sessionStorage.removeItem(LOGGED_OUT_SSO_TOKEN_KEY);
}

const isLoggedOutSsoToken = (token) => {
  return !!token && token === sessionStorage.getItem(LOGGED_OUT_SSO_TOKEN_KEY);
}

export const replaceCurrentHistoryWithoutSsoQuery = () => {
  if (!process.client) return;

  const url = new URL(window.location.href);
  const hasSsoQuery = SSO_QUERY_KEYS.some(key => url.searchParams.has(key));
  if (!hasSsoQuery) return;

  SSO_QUERY_KEYS.forEach(key => url.searchParams.delete(key));
  window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
}

export const authRedirectUrl = (route) => {
  const path = route.path.replace('/', '');
  return path === 'maisoku' || path.substring(0, path.indexOf('/')) === 'maisoku'
    ? process.env.TRUST_LOGIN_MAISOKU_AUTH_URL
    : process.env.TRUST_LOGIN_AGS_AUTH_URL;
}

const authRequest = async (url, options = {}) => {
  if (!url) return null;

  try {
    const token = await getAuthRequestToken();
    const response = await fetch(url, {
      credentials: 'include',
      ...options,
      headers: {
        ...(token ? {Authorization: `Bearer ${token}`} : {}),
        ...(options.body ? {'Content-Type': 'application/json'} : {}),
        ...(options.headers || {}),
      },
    });

    if (!response.ok) return null;
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    return null;
  }
}

const staffMailFromSession = (session) => {
  return session?.mail ?? session?.staff?.mail ?? session?.user?.mail ?? null;
}

const getCachedCurrentStaff = (store) => {
  return store?.getters?.['app/authCurrentStaff'] || cachedCurrentStaff;
}

const setCurrentStaffCache = (staff, store) => {
  cachedCurrentStaff = staff;
  store?.commit('app/setAuthCurrentStaff', staff);
}

export const getCurrentStaff = async ({store, force = false} = {}) => {
  if (!force) {
    const staff = getCachedCurrentStaff(store);
    if (staffMailFromSession(staff)) return staff;
  }

  const staff = await authRequest(process.env.VUE_APP_AUTH_CURRENT_USER_URL);
  if (staffMailFromSession(staff)) {
    setCurrentStaffCache(staff, store);
    clearLoggedOutSsoToken();
    return staff;
  }

  clearCurrentStaffCache(store);
  return null;
}

export const clearAuthenticatedSession = async (store) => {
  clearLegacySsoStorage();
  await authRequest(process.env.VUE_APP_AUTH_LOGOUT_URL, {method: 'POST'});
  clearAuthenticatedCache(store);
}

export const clearAuthenticatedCache = (store) => {
  clearLegacySsoStorage();
  clearPersistedVuexState();
  clearSsoSessionToken();
  clearCurrentStaffCache(store);
  store?.commit('app/resetAuthenticatedState');
}

export default async function ({route, store, app}) {
  const excludeRouteNameList = ['property-property', 'privacy', 'message-line-registration', 'line-tracking'];
  if (excludeRouteNameList.some(routeName => route.name?.includes(routeName))) return;

  clearLegacySsoStorage();

  const hasSsoToken = !!route.query['sso_token'];
  const hasSsoQuery = hasSsoToken || !!route.query['sso_code'];

  // セキュリティ: sso 系クエリは Referer 経由でのトークン漏洩を防ぐため、
  // ネットワークリクエストや外部リダイレクトより前に URL から速やかに除去する。
  // router.replace で履歴エントリを置き換えるため、ブラウザ履歴にもトークンが残らない。
  // （history.replaceState だけだと Vue Router が navigation 完了時に元 URL を再書き込みしてしまうため、
  //   router.replace を使ってルータと URL を一致させる必要がある）
  if (hasSsoQuery) {
    const {sso_token, sso_code, ...queryWithoutSso} = route.query;
    replaceCurrentHistoryWithoutSsoQuery();
    app.router.replace({path: route.path, query: queryWithoutSso, hash: route.hash}).catch(() => {});

    if (isLoggedOutSsoToken(sso_token)) {
      clearAuthenticatedCache(store);
      window.location.replace(authRedirectUrl(route));
      return;
    }

    if (hasSsoToken) {
      clearLoggedOutSsoToken();
      setSsoSessionToken(sso_token);
      clearCurrentStaffCache(store);
    }

    return;
  }

  const staff = await getCurrentStaff({store, force: hasSsoToken});
  if (!staffMailFromSession(staff)) {
    clearAuthenticatedCache(store);
    window.location.replace(authRedirectUrl(route));
    return;
  }
}

export async function getStaffMail(app) {
  const staff = await getCurrentStaff({store: app?.$store});
  return staffMailFromSession(staff);
}
